import React, { useLayoutEffect, useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import MaskReveal from "../../shared/components/UIElements/MaskReveal";
import SmartImage from "../../shared/components/UIElements/SmartImage";
import KineticsDemoChart from "../components/KineticsDemoChart";
import WaterBalanceDemo from "../components/WaterBalanceDemo";

gsap.registerPlugin(ScrollTrigger);

const prefersReducedMotion =
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const CAPABILITIES = [
  {
    title: "Kinetics & Recovery",
    demo: "kinetics",
    alt: "Sample kinetics recovery curves",
    body:
      "Define extraction behaviour per ore type and metal using Forte Dynamics' " +
      "proprietary kinetic formulation — setting ultimate recovery, rate constant " +
      "(k), and kinetic exponent (n). Need more control? Supply a fully custom " +
      "table of daily recovery values to match any extraction profile.",
    pills: [
      "Ultimate Recovery",
      "Rate Constant (k)",
      "Kinetic Exponent (n)",
      "Custom Daily Recoveries",
      "Multi-metal Support",
    ],
    reverse: false,
  },
  {
    title: "Hydrodynamics & 1D Flow",
    image: "/images/home/Leaching Page Main.png",
    alt: "Hydrodynamics and 1D flow configuration",
    body:
      "Solution movement through the heap is modeled using 1D unsaturated flow " +
      "governed by Brooks–Corey constitutive relationships. The engine resolves " +
      "moisture content, permeability, and saturation evolution across multiple " +
      "ore types — coupling flow transport directly to the kinetic recovery " +
      "calculation.",
    pills: [
      "Brooks–Corey Curves",
      "Moisture Content",
      "Permeability",
      "Saturation Evolution",
      "Multi-layer Support",
    ],
    reverse: true,
  },
  {
    title: "Water Balance & Climate",
    demo: "waterbalance",
    alt: "Interactive sample node-based water balance routing diagram",
    body:
      "Site climate data, pond inventory, irrigation rates, runoff, and " +
      "infiltration are integrated into a node-based water circuit model. " +
      "Seasonal and event-driven hydrological dynamics are propagated into the " +
      "flow model to reflect real-world conditions and solution inventory " +
      "constraints.",
    pills: [
      "Node-based Routing",
      "Climate Integration",
      "Pond Management",
      "Infiltration & Runoff",
      "Irrigation Scheduling",
    ],
    reverse: false,
  },
  {
    title: "Stacking & Ore Planning",
    image: "/images/home/Heaps Page - 3D Heap View.png",
    alt: "Heap stacking and ore planning view",
    body:
      "Import mine schedules and stacking plans directly into Orecasting. The " +
      "platform tracks ore type, grade distribution, lift sequencing, and " +
      "blending strategies to simulate the evolving composition of the active " +
      "leach pad over time.",
    pills: [
      "Mine Schedule Import",
      "Lift Sequencing",
      "Ore Blending",
      "Grade Distribution",
      "Segregated Pads",
    ],
    reverse: true,
  },
];

/**
 * CapabilitiesShowcase — the Core Capabilities section.
 *
 * On desktop (and when motion is allowed) the section PINS to the viewport and
 * cross-fades through each capability as you scroll — a little scroll drives a
 * lot of animation ("scroll-telling"). On small screens or with
 * `prefers-reduced-motion`, it falls back to the original stacked rows with the
 * normal scroll-reveal, so touch and motion-sensitive users get a smooth,
 * conventional layout.
 */
export default function CapabilitiesShowcase() {
  const sectionRef = useRef(null);
  const innerRef = useRef(null);
  const slideEls = useRef([]);
  const triggerRef = useRef(null);
  const [pinned, setPinned] = useState(false);
  const [active, setActive] = useState(0);

  useLayoutEffect(() => {
    if (prefersReducedMotion) return undefined;
    if (!window.matchMedia("(min-width: 901px)").matches) return undefined;

    const slides = slideEls.current.filter(Boolean);
    if (slides.length < 2) return undefined;

    // Switch to pinned layout first; build the ScrollTrigger on the next frame
    // so it measures the *pinned* geometry (not the fallback rows).
    setPinned(true);

    let ctx;
    const rafId = requestAnimationFrame(() => {
      ctx = gsap.context(() => {
        gsap.set(slides, { opacity: 0 });
        gsap.set(slides[0], { opacity: 1 });

        triggerRef.current = ScrollTrigger.create({
          trigger: sectionRef.current,
          start: "top 64px", // pin just below the fixed 64px site header
          end: () => "+=" + slides.length * window.innerHeight,
          pin: innerRef.current,
          pinSpacing: true,
          scrub: 0.4,
          anticipatePin: 1,
          invalidateOnRefresh: true,
          onUpdate: (self) => {
            const n = slides.length;
            const pos = self.progress * (n - 1); // 0 .. n-1
            const HOLD = 0.34; // full-opacity radius around each capability
            const FADE = 0.55; // cross-fade span
            slides.forEach((el, i) => {
              const d = Math.abs(pos - i);
              let op;
              if (d <= HOLD) op = 1;
              else if (d >= HOLD + FADE) op = 0;
              else op = 1 - (d - HOLD) / FADE;
              el.style.opacity = String(op);
              el.style.transform = `translateY(${(pos - i) * 22}px)`;
              el.style.pointerEvents = op > 0.5 ? "auto" : "none";
            });
            setActive(Math.round(pos));
          },
        });
      }, sectionRef);
      ScrollTrigger.refresh();
    });

    // Recompute measurements once late-loading images settle.
    const refresh = () => ScrollTrigger.refresh();
    window.addEventListener("load", refresh);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("load", refresh);
      if (ctx) ctx.revert();
      triggerRef.current = null;
      setPinned(false);
    };
  }, []);

  // Rail navigation: jump to a capability's slice of the pinned scroll range.
  const goTo = (i) => {
    const st = triggerRef.current;
    if (!st || !window.__lenis) return;
    const n = CAPABILITIES.length;
    const y = st.start + (i / (n - 1)) * (st.end - st.start);
    window.__lenis.scrollTo(y);
  };

  return (
    <section
      ref={sectionRef}
      className={`cap-showcase capabilities-showcase${pinned ? " is-pinned" : ""}`}
      aria-labelledby="capabilities-heading"
    >
      <div ref={innerRef} className="cap-inner">
        <MaskReveal as="h2" id="capabilities-heading" className="section-heading">
          Core Capabilities
        </MaskReveal>

        <div className="cap-stage">
          {CAPABILITIES.map((c, i) => (
            <div
              key={c.title}
              ref={(el) => {
                slideEls.current[i] = el;
              }}
              className={`showcase-row cap-slide${
                c.reverse ? " showcase-row--reverse" : ""
              }`}
            >
              <div className="showcase-image">
                {c.demo === "kinetics" && <KineticsDemoChart />}
                {c.demo === "waterbalance" && <WaterBalanceDemo />}
                {!c.demo && <SmartImage src={c.image} alt={c.alt} />}
              </div>
              <div className="showcase-content">
                <h3>{c.title}</h3>
                <p>{c.body}</p>
                <ul className="showcase-pills">
                  {c.pills.map((p) => (
                    <li key={p}>{p}</li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {pinned && (
          <div className="cap-rail">
            {CAPABILITIES.map((c, i) => (
              <button
                key={c.title}
                type="button"
                className={`cap-rail__item${i === active ? " is-active" : ""}`}
                onClick={() => goTo(i)}
                aria-label={`Go to ${c.title}`}
              >
                <span className="cap-rail__dot" />
                <span className="cap-rail__label">{c.title}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
